import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { ClientDocument } from "@visitproof/types";
import { DocumentService } from "../services/DocumentService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";

// Re-type for backward compatibility
type Document = ClientDocument;

interface DocumentContextProps {
  documentList: Document[];
  selectedDocument: Document | null;
  setSelectedDocument: (documentId: string) => void;
  createDocument: (
    documentData: Omit<
      Document,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ) => Promise<string>;
  updateDocument: (
    documentId: string,
    documentData: Partial<Omit<Document, "id" | "createdAt" | "updatedAt">>,
  ) => void;
  deleteDocumentById: (documentId: string) => void;
  reloadDocumentList: () => void;
}

const DocumentContext = createContext<DocumentContextProps | undefined>(
  undefined,
);

export const useDocument = (): DocumentContextProps => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error("useDocument must be used within a DocumentProvider");
  }
  return context;
};

export const DocumentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { account } = useAccount();
  const [documentList, setDocumentList] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );

  const reloadDocumentList = useCallback(async () => {
    if (account?.id) {
      const documentList = await DocumentService.getAllDocuments(account.id);
      setDocumentList(documentList);
    }
  }, [account?.id]);

  useEffect(() => {
    reloadDocumentList();
  }, [reloadDocumentList]);

  const setSelectedDocumentById = async (documentId: string) => {
    const documentData = await DocumentService.getDocument(documentId);
    setSelectedDocument(documentData);
  };

  const createDocument = async (
    documentData: Omit<
      Document,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!account?.id || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const newDocumentId = await DocumentService.createDocument({
      ...documentData,
      accountId: account.id,
      createdBy: user.uid,
    });
    await reloadDocumentList();
    return newDocumentId;
  };

  const updateDocument = async (
    documentId: string,
    updatedData: Partial<Omit<Document, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await DocumentService.updateDocument(documentId, updatedData);
    await reloadDocumentList();
  };

  const deleteDocumentById = async (documentId: string) => {
    await DocumentService.deleteDocument(documentId);
    await reloadDocumentList();
  };

  return (
    <DocumentContext.Provider
      value={{
        documentList,
        selectedDocument,
        createDocument,
        updateDocument,
        deleteDocumentById,
        setSelectedDocument: setSelectedDocumentById,
        reloadDocumentList,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
};
